import React, { useState, useEffect } from 'react';

import ManualEntry from '../Wizard/Address/ManualEntry';
import AutoEntry from '../Wizard/Address/AutoEntry';
import Section from './Section';
import { gql } from 'apollo-boost';
import {useApolloClient, useMutation} from '@apollo/react-hooks';
import Modal from '../Modal/Modal';
import Button from '../Form/Button/Button';
import ManualEntryv2 from "../Wizard/Address/ManualEntryv2";
import withFeatureFlags from "../FeatureFlags/withFeatureFlags";

/** @import {ShowValidation} from '../Wizard/Address/ManualEntryv2Types' */
/** @import {AddressResult, Address} from '../Wizard/Address/AutoEntryTypes' */
/** @import {ValueType} from 'react-select' */
/** @import {OptionType} from '../Form/Select/SelectTypes' */
/** @import {AddressProps} from './AddressTypes' */
/** @import {WithFeatureFlagsProps} from '../FeatureFlags/FeatureFlagsTypes' */

const UPDATE_ADDRESS = gql`
  mutation UpdateAddress($request : AddressInputType!){
    updateAddress(request:$request) {
      success
    }
  }  
`;

const ADDRESS_MATCHES = gql`
    query FindAddressesByPostcode2($postCode: String!) {
        addressMatches(postCode: $postCode) {
            hasGbMatches
            hasNonGbMatches
        }
    }  
`;

function Address(/** @type {AddressProps & WithFeatureFlagsProps} */ props) {
    const client = useApolloClient();
    
    const [fieldValues, setFieldValues] = useState(/** @type {Record<string, string>} */ (props.profile.address || {}));
    const [editing, setEditing] = useState(false);
    const [manualEntry, setManualEntry] = useState(false);
    const [showValidation, setShowValidation] = useState(/** @type {ShowValidation} */ ({ showValidation: false, errors: [] }));
    const [successModalOpen, setSuccessModalOpen] = useState(false);
    const [selectedAddress, setSelectedAddress] = useState(/** @type {Address | null} */ (null));

    const getAddressMatches = async (/** @type {{variables: Record<string, string>}} */ options) => {
        let res = await client.query({
                query: ADDRESS_MATCHES,
                variables: options.variables
            });
        
        return res;
        
    }
    
    const [updateAddress] = useMutation(UPDATE_ADDRESS);

    useEffect(() => {
        setFieldValues(props.profile.address || []);
    }, []);

    const handleUpdateField = (/** @type {string} */ name) => (/** @type {string | ValueType<OptionType, false>} */ value) => {
        setShowValidation({ showValidation: false, errors: [] });

        const valueAsString = /** @type {string} */(value); // fighting the typescript compiler
        setFieldValues({ ...fieldValues, [name]: valueAsString })
    }

    const setEditingCallback = (/** @type {boolean} */ editing) => {
        setEditing(editing);
        setManualEntry(false);
    };

    const setManualEntryCallback = (/** @type {boolean} */ manualEntry) => {
        setManualEntry(manualEntry);
        setFieldValues({
            country: 'GBR'
        });
    }

    const onAddressSelected = (/** @type {AddressResult | undefined} */ address) => {
        if (address && address.address) {
            setFieldValues({...address.address});
            setSelectedAddress(address.address);
            setShowValidation({ showValidation: false, errors: [] });
        }
    };

    const renderAutoEntry = () => {
        return (
            <AutoEntry
                onSetManualEntry={setManualEntryCallback}
                disabled={!editing}
                fieldValues={fieldValues}
                handleUpdateField={handleUpdateField}
                onAddressSelected={onAddressSelected}
            />
        );
    }

    const renderManualEntry = () => {
        if (props.featureEnabled('EnableManualEntryUpdates')) {
            return (
                <ManualEntryv2
                    disabled={!editing}
                    fieldValues={fieldValues}
                    handleUpdateField={handleUpdateField}
                    showValidation={showValidation}
                />
            );
        }
            return (
                <ManualEntry
                    disabled={!editing}
                    fieldValues={fieldValues}
                    handleUpdateField={handleUpdateField}
                    showValidation={showValidation}
                />
            )
    }

    const shouldEnterManually = () => {
        return !editing || manualEntry;
    }

    const cancelEditing = () => {
        setShowValidation({
            showValidation: false,
            errors: []
        });
        setFieldValues(props.profile.address);
        setManualEntry(false);
        setSelectedAddress(null);
    }

    const approveEditing = async () => {
        var result = await updateAddress({
            variables: {
                request: {
                    buildingName: fieldValues.buildingName,
                    buildingNumber: fieldValues.buildingNumber,
                    country: fieldValues.country,
                    flatNumber: fieldValues.flatNumber,
                    postCode: fieldValues.postCode,
                    streetLine1: fieldValues.streetLine1,
                    streetLine2: fieldValues.streetLine2,
                    town: fieldValues.town
                }
            }
        });

        props.onSave && props.onSave({address: fieldValues, manualEntry: false});
        // props.profile.address = fieldValues; // COMMENTED AS YOU SHOULD NEVER DO THIS - DISCUSS WITH BRADLEY

        setSuccessModalOpen(true);
        setSelectedAddress(null);
    }
    
    async function checkAddy() {
        return await getAddressMatches({
            variables: {
                postCode: fieldValues.postCode
            }
        });
    }

    const addressValidation = async () => {
        const address = fieldValues;

        let result = await checkAddy();

        let data = result.data;
        let invalidPostCode = !data || !data.addressMatches || (data.addressMatches.hasNonGbMatches && !data.addressMatches.hasGbMatches);

        if (!shouldEnterManually() && !selectedAddress) {
            setShowValidation({
                showValidation: true,
                errors: [
                    "invalidForm"
                ]
                
            });
            return false;
        }

        if (!address || !address.streetLine1 || !address.town || !address.postCode || address.country !== 'GBR') {
            setShowValidation({
                showValidation: true,
                errors: [
                    "invalidForm"
                ]
            });
            return false;
        }

        if (!address.flatNumber && !address.buildingNumber && !address.buildingName) {
            setShowValidation({
                showValidation: true,
                errors: [
                    "invalidForm"
                ]
            });
            return false;
        }

        if (invalidPostCode) {
            setShowValidation({
                showValidation: false,
                errors: [
                    "invalidAddress"
                ]
            });
            return false;
        }
            
        return true;
    };

    const onModalComplete = () => {
        setSuccessModalOpen(false);
    };

    const renderSuccessModal = () => {
        return (
            <Modal onClose={onModalComplete} className="modal--fixed-height" ignoreClickOutside={true}>
                <>
                    <h4 className="modal__success-title">Success!</h4>
                    <p className="modal__paragraph">Your address has been updated.</p>
                    <div className="modal__spacer" />
                    <Button size='med' customClassNames={["modal__button", "modal__button--left"]} label="Done" onClick={onModalComplete} />
                </>
            </Modal>
        );
    }

    return (
        <Section title="Address" modifiers={['margin-bottom']} editing={editing} setEditing={setEditingCallback} onCancel={cancelEditing} onSave={approveEditing} validation={addressValidation} valid={true}>
            {shouldEnterManually() ? renderManualEntry() : renderAutoEntry()}
            {successModalOpen && renderSuccessModal()}
        </Section>
    )
};

export default withFeatureFlags(Address);
